// src/components/AdminPortal/UserManagement/UserApplications.js
import { useState, useEffect } from "react";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import "../../User.css";
import "./UserApplication.css";
import ContentLoader from "../../../global/Content/ContentLoader";
import Axios from "../../../utils/axios";

const UserApplications = () => {
  const [userApplications, setUserApplications] = useState([]);
  const [applications, setApplications] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [selectedUserApp, setSelectedUserApp] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState("");
  const [assignedRoles, setAssignedRoles] = useState([]);

  // ✅ Fetch all user applications
  const fetchUserApplications = async () => {
    try {
      setLoading(true);
      const res = await Axios.get(`/api/userApplication/getAllUserApplication`);
      setUserApplications(res.data || []);
    } catch (err) {
      toast.error("Failed to fetch user applications");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch all applications
  const fetchApplications = async () => {
    try {
      const res = await Axios.get(`/api/application/getAllApplications`);
      setApplications(res.data || []);
    } catch (err) {
      toast.error("Failed to fetch user applications");
    }
  };

  useEffect(() => {
    const fetchRolesForApplication = async () => {
      if (!selectedApplication) return;

      try {
        const res = await Axios.get(
          `//api/userApplicationRole/getUserApplicationRolesByUserApplicationId?applicationId=${selectedApplication}`
        );
        setRoles(res.data || []);
      } catch (err) {
        toast.error("Failed to fetch roles for the selected application");
      }
    };

    fetchRolesForApplication();
  }, [selectedApplication]);

  // ✅ Fetch all roles
  const fetchRoles = async () => {
    try {
      const res = await Axios.get(
        `/api/userApplicationRole/getAllUserApplicationRoles`
      );
      setRoles(res.data || []);
    } catch (err) {
      toast.error("Failed to fetch user applications");
    }
  };

  useEffect(() => {
    fetchUserApplications();
    fetchApplications();
    // fetchRoles();
  }, []);

  // ✅ Open Manage Modal (fetch roles by ApplicationRoleId if available)
  const openManageModal = async (ua) => {
    try {
      let userAppRole = null;

      if (ua.applicationRoleId) {
        const res = await Axios.get(
          `/api/userApplicationRole/getUserApplicationRoleById?id=${ua.applicationRoleId}`
        );
        userAppRole = res.data;
      }

      setSelectedUserApp(ua);
      setSelectedApplication(ua.applicationId || "");
      setAssignedRoles(userAppRole?.roles || []);
      setIsManageOpen(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch roles for this user application");
    }
  };

  // ✅ Toggle role selection
  const toggleRole = (roleName) => {
    setAssignedRoles((prev) =>
      prev.includes(roleName)
        ? prev.filter((r) => r !== roleName)
        : [...prev, roleName]
    );
  };

  // ✅ Save roles (create or update)
  const saveRoles = async () => {
    try {
      if (!selectedUserApp) return;

      const payload = {
        userApplicationId: selectedUserApp.id,
        applicationId: selectedApplication,
        roles: assignedRoles,
      };

      if (selectedUserApp.applicationRoleId) {
        // Update
        await Axios.put(
          `/api/userApplicationRole/updateUserApplicationRole?id=${selectedUserApp.applicationRoleId}`,
          payload
        );
        toast.success("Roles updated successfully");
      } else {
        // Create
        await Axios.post(
          `/api/userApplicationRole/createUserApplicationRole`,
          payload
        );
        toast.success("Roles assigned successfully");
      }

      fetchUserApplications();
      setIsManageOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save roles");
    }
  };

  // ✅ Delete user application role
  const deleteRole = async (id) => {
    try {
      await Axios.delete(
        `/api/userApplicationRole/deleteUserApplicationRole?id=${id}`
      );
      toast.success("Role deleted successfully");
      fetchUserApplications();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete role");
    }
  };

  // ✅ Filter search
  const filteredApps = userApplications.filter((ua) => {
    const term = searchTerm?.toLowerCase() || "";
    return (
      ua.username?.toLowerCase().includes(term) ||
      ua.applicationName?.toLowerCase().includes(term) ||
      ua.applicationRoleName?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="userapplications-container">
      <h2>User Applications</h2>
      <br />

      <div className="search-and-new">
        <input
          type="text"
          placeholder="Search by Username, Application or Role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <button
          onClick={() => setSearchTerm("")}
          className="clear-button"
          style={{ marginRight: "10px" }}
        >
          Clear
        </button>
      </div>

      {loading ? (
        <div className="loading">
          <ContentLoader />{" "}
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Application</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredApps.length === 0 ? (
              <tr>
                <td colSpan="4">No results found</td>
              </tr>
            ) : (
              filteredApps.map((ua) => (
                <tr key={ua.id}>
                  <td>{ua.username}</td>
                  <td>{ua.applicationName}</td>
                  <td>{ua.applicationRoleName || "—"}</td>
                  <td>
                    <button onClick={() => openManageModal(ua)}>
                      <FiEdit /> Manage
                    </button>
                    {ua.applicationRoleId && (
                      <button onClick={() => deleteRole(ua.applicationRoleId)}>
                        <FiTrash2 /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {/* Manage Modal */}
      {isManageOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Manage Roles</h2>
              <button onClick={() => setIsManageOpen(false)}>&times;</button>
            </div>

            <div className="form-group">
              <label>Application</label>
              <select
                value={selectedApplication}
                onChange={(e) => setSelectedApplication(e.target.value)}
              >
                <option value="">Select Application</option>
                {applications.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.applicationName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Roles</label>
              {roles.map((role) => (
                <div key={role.id}>{role.applicationRoleName}</div>
              ))}
            </div>

            <div className="form-actions">
              <button onClick={() => setIsManageOpen(false)}>Cancel</button>
              <button onClick={saveRoles}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserApplications;
